/**
 * Cookie / tracking consent gate.
 *
 * @overview
 * Single decision point for whether ANY analytics or advertising tag may run.
 * Both PostHog (`@/lib/posthog`) and the Meta Pixel (`@/lib/metaPixel`) ask
 * this module before they initialise, so consent is enforced in one place
 * rather than re-implemented per vendor.
 *
 * Until this shipped, PostHog initialised unconditionally on every page load.
 * Adding a Meta Pixel — an advertising tag, not just product analytics — is
 * what makes an explicit opt-in gate necessary rather than merely tidy.
 *
 * @usage
 * ```typescript
 * import { hasAnalyticsConsent, setConsent, onConsentChange } from '@/lib/consent';
 *
 * if (hasAnalyticsConsent()) initPostHog();
 * onConsentChange((state) => { if (state === 'granted') initPostHog(); });
 * ```
 *
 * @security Stores a single non-identifying enum. No user data, no fingerprint.
 */

/** Bumped when the wording or scope of the request materially changes. */
const CONSENT_STORAGE_KEY = 'iv_tracking_consent_v1';

export type ConsentState = 'granted' | 'denied' | 'unset';

type ConsentListener = (state: ConsentState) => void;

const listeners = new Set<ConsentListener>();

/**
 * Read the stored decision.
 *
 * Every storage access is wrapped: Safari private mode, disabled site data, and
 * embedded webviews all throw on `localStorage` access rather than returning
 * null. A throw here must degrade to "no consent", never break the page.
 */
export function getConsent(): ConsentState {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return raw === 'granted' || raw === 'denied' ? raw : 'unset';
  } catch {
    return 'unset';
  }
}

/**
 * True only for an explicit grant.
 *
 * Fails closed: an undecided visitor is treated exactly like a refusing one, so
 * a banner that never renders cannot silently enable tracking.
 */
export function hasAnalyticsConsent(): boolean {
  return getConsent() === 'granted';
}

/** Record the visitor's decision and notify subscribers. */
export function setConsent(state: Exclude<ConsentState, 'unset'>): void {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, state);
  } catch {
    // Storage unavailable. Still notify, so tags respond within this pageview
    // even though the choice will not survive a reload.
  }
  notify(state);
}

/**
 * Clear the decision, returning the visitor to undecided.
 *
 * Exists for the privacy-policy "withdraw consent" control; withdrawal has to
 * be as easy as granting for the opt-in to be meaningful.
 */
export function resetConsent(): void {
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Ignore — see getConsent().
  }
  notify('unset');
}

/**
 * Subscribe to consent changes. Returns an unsubscribe function.
 *
 * Tags initialise lazily on grant so a visitor who accepts mid-session starts
 * being measured immediately, without a reload.
 */
export function onConsentChange(listener: ConsentListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify(state: ConsentState): void {
  for (const listener of listeners) {
    try {
      listener(state);
    } catch {
      // One bad subscriber must not stop the others from being told.
    }
  }
}

/** Test seam: drop all subscribers. Not used by application code. */
export function __resetConsentListenersForTest(): void {
  listeners.clear();
}
